import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import {
  INVENTORY_PERMISSIONS,
  PRODUCT_PERMISSIONS,
} from '@invento/shared';
import { PermissionGuard } from '@/navigation/guards/permission-guard';
import { ROUTE_PATHS } from '../constants';
import { lazyNamed } from '../lazy-named';
import { TenantAppRouteFrame } from './route-frames';
import { CompatibilityRedirect, compatibilityRedirectState } from '../compatibility-redirect';

const DashboardPage = lazyNamed(() => import('@/features/dashboard'), 'DashboardPage');

const loadInventory = () => import('@/features/inventory');
const InventoryAdjustStockPage = lazyNamed(loadInventory, 'InventoryAdjustStockPage');
const InventoryListPage = lazyNamed(loadInventory, 'InventoryListPage');
const InventoryProductDetailPage = lazyNamed(loadInventory, 'InventoryProductDetailPage');
const InventoryProductFormPage = lazyNamed(loadInventory, 'InventoryProductFormPage');
const InventoryReceiveStockPage = lazyNamed(loadInventory, 'InventoryReceiveStockPage');
const InventoryTransferDetailPage = lazyNamed(loadInventory, 'InventoryTransferDetailPage');
const InventoryTransferSendPage = lazyNamed(loadInventory, 'InventoryTransferSendPage');
const InventoryVariantFormPage = lazyNamed(loadInventory, 'InventoryVariantFormPage');

const loadSettings = () => import('@/features/settings');
const HelpArticlePage = lazyNamed(loadSettings, 'HelpArticlePage');
const HelpPage = lazyNamed(loadSettings, 'HelpPage');
const SettingsPage = lazyNamed(loadSettings, 'SettingsPage');
const MenuPage = lazyNamed(() => import('../pages'), 'MenuPage');

function LegacyProductDetailRedirect() {
  const { productId } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`${productId ? ROUTE_PATHS.INVENTORY.PRODUCT_DETAIL(productId) : ROUTE_PATHS.INVENTORY.ROOT}${location.search}${location.hash}`}
      replace
      state={compatibilityRedirectState(
        'moved',
        location.pathname,
        location.pathname,
        'Inventory products',
      )}
    />
  );
}

function LegacyProductEditRedirect() {
  const { productId } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`${productId ? ROUTE_PATHS.INVENTORY.PRODUCT_DETAIL(productId) : ROUTE_PATHS.INVENTORY.ROOT}${location.search}${location.hash}`}
      replace
      state={compatibilityRedirectState(
        'renamed',
        location.pathname,
        location.pathname,
        'Inventory products',
      )}
    />
  );
}

function LegacyOrderDetailRedirect() {
  const { id } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`${id ? `/sales/orders/${id}` : '/sales/orders'}${location.search}${location.hash}`}
      replace
      state={compatibilityRedirectState('merged', location.pathname, location.pathname, 'Sales orders')}
    />
  );
}

export function appRoutes() {
  return (
    <Route element={<TenantAppRouteFrame />}>
      <Route path="dashboard" element={<DashboardPage />} />

      {/* Inventory / Product flow */}
      <Route
        path="inventory"
        element={
          <PermissionGuard requiredAny={[PRODUCT_PERMISSIONS.READ, INVENTORY_PERMISSIONS.READ]}>
            <InventoryListPage />
          </PermissionGuard>
        }
      />
      <Route
        path="inventory/products/new"
        element={
          <PermissionGuard required={PRODUCT_PERMISSIONS.CREATE}>
            <InventoryProductFormPage />
          </PermissionGuard>
        }
      />
      <Route
        path="inventory/products/:productId"
        element={
          <PermissionGuard requiredAny={[PRODUCT_PERMISSIONS.READ, INVENTORY_PERMISSIONS.READ]}>
            <InventoryProductDetailPage />
          </PermissionGuard>
        }
      />
      <Route
        path="inventory/products/:productId/variants/new"
        element={
          <PermissionGuard required={PRODUCT_PERMISSIONS.CREATE}>
            <InventoryVariantFormPage mode="add" />
          </PermissionGuard>
        }
      />
      <Route
        path="inventory/products/:productId/variants/:variantId/edit"
        element={
          <PermissionGuard required={PRODUCT_PERMISSIONS.UPDATE}>
            <InventoryVariantFormPage mode="edit" />
          </PermissionGuard>
        }
      />
      <Route
        path="inventory/receive"
        element={
          <PermissionGuard required={INVENTORY_PERMISSIONS.RECEIVE}>
            <InventoryReceiveStockPage />
          </PermissionGuard>
        }
      />
      <Route
        path="inventory/adjust"
        element={
          <PermissionGuard required={INVENTORY_PERMISSIONS.ADJUST}>
            <InventoryAdjustStockPage />
          </PermissionGuard>
        }
      />
      <Route
        path="inventory/transfers/new"
        element={
          <PermissionGuard
            requiredAll={[
              INVENTORY_PERMISSIONS.TRANSFER_CREATE,
              INVENTORY_PERMISSIONS.STOCK_CROSS_BRANCH_TRANSFER,
            ]}
          >
            <InventoryTransferSendPage />
          </PermissionGuard>
        }
      />
      <Route
        path="inventory/transfers/:transferId"
        element={
          <PermissionGuard required={INVENTORY_PERMISSIONS.READ}>
            <InventoryTransferDetailPage />
          </PermissionGuard>
        }
      />

      {/* /products compatibility only. Do not mount a second product surface. */}
      <Route
        path="products"
        element={
          <CompatibilityRedirect to={ROUTE_PATHS.INVENTORY.ROOT} variant="moved" toLabel="Inventory products" />
        }
      />
      <Route
        path="products/new"
        element={
          <CompatibilityRedirect to={ROUTE_PATHS.INVENTORY.NEW_PRODUCT} variant="renamed" toLabel="Inventory products" />
        }
      />
      <Route
        path="products/create"
        element={
          <CompatibilityRedirect to={ROUTE_PATHS.INVENTORY.NEW_PRODUCT} variant="renamed" toLabel="Inventory products" />
        }
      />
      <Route path="products/:productId" element={<LegacyProductDetailRedirect />} />
      <Route path="products/:productId/edit" element={<LegacyProductEditRedirect />} />

      {/* Legacy Orders are compatibility redirects only. Sales Orders is canonical. */}
      <Route
        path="orders"
        element={
          <CompatibilityRedirect to="/sales/orders" variant="merged" toLabel="Sales orders" />
        }
      />
      <Route
        path="orders/create"
        element={
          <CompatibilityRedirect to="/sales/orders/new" variant="renamed" toLabel="Sales orders" />
        }
      />
      <Route path="orders/:id" element={<LegacyOrderDetailRedirect />} />

      {/* Settings, Help, and Menu stay tenant-ready but not permission-gated. */}
      <Route path="settings" element={<SettingsPage />} />
      <Route path="help" element={<HelpPage />} />
      <Route path="help/articles/:articleId" element={<HelpArticlePage />} />
      <Route path="menu" element={<MenuPage />} />
    </Route>
  );
}
